import dayjs from 'dayjs'
import { StartDateRange } from 'features/contracts/add/types'
import { DATE_FORMAT, ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import { Situation, SITUATIONS } from '@boltenergy-be/shared'

/**
 * Returns if a given postal code is valid
 *
 * @param postalCode
 * @returns boolean
 */
export const isValidPostalCode = (postalCode?: number): boolean => {
  return postalCode?.toString()?.length === 4
}

/**
 * Turns VAT number to company number (removes 'BE')
 *
 * @param {string} vatNumber
 * @returns {string}
 */
export const vatNumberToCompanyNumber = (vatNumber: string): string => {
  return vatNumber.replace('BE', '')
}

/**
 * Returns the date range for the start date based on the given situation, hasForcedDate, isMoveFlow & isSuperUser parameters
 *
 * @param {Situation} situation
 * @returns {StartDateRange}
 */
export const getStartDateRange = (situation: Situation): StartDateRange => {
  const now = dayjs(undefined)

  switch (situation) {
    case SITUATIONS.newConnection: {
      return {
        min: now.format(ISO8601_DATE_FORMAT),
        max: now.format(ISO8601_DATE_FORMAT),
        initial: now.format(ISO8601_DATE_FORMAT)
      }
    }

    case SITUATIONS.switch: {
      return {
        min: now.add(25, 'days').format(ISO8601_DATE_FORMAT),
        max: now.add(12, 'months').format(ISO8601_DATE_FORMAT),
        initial: now.add(25, 'days').format(ISO8601_DATE_FORMAT)
      }
    }

    case SITUATIONS.move: {
      return {
        min: now.subtract(25, 'days').format(ISO8601_DATE_FORMAT),
        max: now.add(10, 'months').format(ISO8601_DATE_FORMAT),
        initial: now.format(ISO8601_DATE_FORMAT)
      }
    }

    default:
      return {
        min: now.format(ISO8601_DATE_FORMAT),
        max: now.format(ISO8601_DATE_FORMAT),
        initial: now.format(ISO8601_DATE_FORMAT)
      }
  }
}

/**
 * Checks if text has no numbers in it
 *
 * @param {string} text
 * @returns {boolean}
 */
export const hasNoNumber = (text: string): boolean => {
  return !/\d/.test(text)
}

/**
 * Formats a given date to a string in the dd/MM/yyyy format
 *
 * @param {Date} date
 * @returns {string}
 */
export const dateToString = (date: Date | string): string => {
  return dayjs(date).format(DATE_FORMAT)
}

/**
 * Returns if the given barcode string is a valid EAN number
 *
 * @param {string} barcode
 * @returns {boolean}
 */
export const isValidEAN = (barcode: string): boolean => {
  // Check if EAN is 18 characters long
  if (barcode.length !== 18) return false

  // Check if the EAN starts with 54
  if (!barcode.startsWith('54')) return false

  // Check if EAN is a number
  if (isNaN(parseInt(barcode, 10))) return false

  // Check if the last digit is a number
  const lastDigit = parseInt(barcode.substring(barcode.length - 1), 10)
  if (isNaN(lastDigit)) return false

  // Reverse the all the numbers except the last
  const arr = barcode
    .substring(0, barcode.length - 1)
    .split('')
    .reverse()

  // Calculate the sum of all the numbers
  // Numbers on even positions are multiplied by 3 and then added
  // Numbers on odd positions are added without being multiplied
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    if (i % 2 === 0) {
      sum += parseInt(arr[i], 10) * 3
    } else {
      sum += parseInt(arr[i], 10)
    }
  }

  // Calculate the checksum
  let checkSum = sum % 10

  // Check & return if the checksum equals the last digit if the checksum equals 0
  if (checkSum === 0) return checkSum === lastDigit

  // Recalculate the checksum & check if last digit matches if checksum didn't equal 0
  checkSum = 10 - (sum % 10)

  return checkSum === lastDigit
}
